import { apiRequest } from "./client";
import { activeExam, adoptActiveExam, type ProfileExam } from "../lib/activeExam";

export interface ExamProfile {
  examType: ProfileExam;
  examYear: number | null;
  onboardingCompleted: boolean;
  isPrimary: boolean;
}

export interface MeResponse {
  student: {
    id: string;
    firstName: string | null;
    email: string | null;
    phoneNumber: string | null;
    streakDays: number;
    onboardingCompleted: boolean;
    examType: string | null;
    examYear: number | null;
  };
  entitlement: { hasFullAccess: boolean; daysRemaining: number; fromLegacyPurchase: boolean };
  trial: {
    used: number;
    remaining: number;
    exhausted: boolean;
    shouldWarn: boolean;
    limit: number;
  };
  readiness: { overall: number | null; band: string; confidence: number };
  subjects: {
    subjectId: string;
    name: string;
    readiness: number | null;
    confidence: number;
    band: string;
    weakTopics: string[];
  }[];
  /** The exams this student sits, primary first. Two when they sit both. */
  exams?: ExamProfile[];
  /** The profile everything above belongs to. Null before they pick an exam. */
  activeExam?: ProfileExam | null;
}

/**
 * Everything Home needs, in one call — for the active exam profile.
 *
 * The server says which profile it answered for; the phone adopts it, so a
 * fresh install, or a stored exam the student no longer sits, settles on what
 * the server actually showed.
 */
export async function fetchMe(): Promise<MeResponse> {
  const asked = activeExam();
  const me = await apiRequest<MeResponse>("/api/me", { authenticated: true });
  // Only if the student has not switched while this was in flight: a slow
  // JAMB answer arriving after a tap on WAEC must not switch them back.
  if (me.activeExam && activeExam() === asked) adoptActiveExam(me.activeExam);
  return me;
}

/** Start preparing for a second exam, from Profile. Its subjects come next. */
export function addExam(
  examType: ProfileExam,
  examYear: number,
): Promise<{ exam: ExamProfile; exams: ExamProfile[] }> {
  return apiRequest<{ exam: ExamProfile; exams: ExamProfile[] }>("/api/me/exams", {
    method: "POST",
    body: { examType, examYear },
    authenticated: true,
  });
}

/** Soft-delete the student's own account. */
export function deleteAccount(): Promise<{ ok: true }> {
  return apiRequest<{ ok: true }>("/api/me/account", {
    method: "DELETE",
    authenticated: true,
  });
}
